import { contractService } from 'content/service';
import { ContractInterface } from 'contract/type';
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { BASE_URL, CONTRACT_EXPORT_API_PATH } from 'constants/api';
import axios from 'axios';
import * as Component from '../../../../shared/components';
import { getContractColumns } from './ContractColumns';
import { uploadHandlers } from './handlers';
import tableHandlers from './handlers/tableHandlers';

export default function Contract(): React.JSX.Element {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchContracts = async (
    params: URLSearchParams
  ): Promise<{ data: ContractInterface[]; count: number }> => {
    const contractList = await contractService.fetch(params);
    return { data: contractList.results, count: contractList.count };
  };

  const { handleOnClickDeleteButton, handleOnClickTitle, handleOnClickSetting, handleAddClick } =
    tableHandlers({
      navigate,
    });

  uploadHandlers();

  const columns = getContractColumns({
    handleOnClickTitle,
    handleOnClickSetting,
    handleOnClickDeleteButton,
    searchParams,
  });

  const handleExcelDownload = async (): Promise<void> => {
    try {
      message.loading('Downloading contracts...', 0);

      // 현재 searchParams를 사용하여 다운로드 API 호출
      const downloadParams = new URLSearchParams(searchParams);
      // 페이지네이션 관련 파라미터 제거 (전체 다운로드)
      downloadParams.delete('page');
      downloadParams.delete('page_size');

      const url = `${BASE_URL}${CONTRACT_EXPORT_API_PATH}`;

      // axios를 사용하여 파일 다운로드 (인증 정보 자동 포함)
      const response = await axios.get(url, {
        params: downloadParams,
        responseType: 'blob',
      });

      // response header에서 파일명 추출
      const contentDisposition = response.headers['content-disposition'];
      let filename = `contracts_${new Date().toISOString().split('T')[0]}.csv`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      // blob으로 다운로드
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // 메모리 정리
      window.URL.revokeObjectURL(downloadUrl);

      message.destroy();
      message.success('Contract download completed');
    } catch (error) {
      message.destroy();
      message.error(`Failed to download contracts: ${error}`);
    }
  };

  const ExcelDownloadButton = (): JSX.Element => {
    return (
      <Component.CustomButtonComponent
        text={`CSV Download`}
        icon={<DownloadOutlined />}
        onClick={handleExcelDownload}
        size={'small'}
        type="default"
      />
    );
  };

  const AddButton = (): JSX.Element => {
    return (
      <Component.CustomButtonComponent
        text={`+ Add Contract`}
        onClick={handleAddClick}
        size={'small'}
      />
    );
  };

  return (
    <Component.BasePageComponent>
      <Component.TableComponent<ContractInterface>
        name="Contract"
        dataFetch={fetchContracts}
        columns={columns}
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        additionalButton={[ExcelDownloadButton(), AddButton()]}
      />
    </Component.BasePageComponent>
  );
}
